import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { GridColumnProps } from "@progress/kendo-react-grid";

import i18n from "i18n";
import { AccountStatement } from "interface/accounting";
import { GrideDateFormatter } from "ui/Date";
import { NumericalCell, NumericalHeader } from "ui/grid/GridCommandCell";
import { CLASSNAME } from "utils/constant";
import { formatNorwegianNumber } from "utils/helper";

const t = i18n.t;

const dateColumn = (
	field: string,
	title: string,
	width?: number,
): GridColumnProps => ({
	field,
	title,
	width: width,
	filterable: false,
	cells: {
		data: (props) =>
			props.dataItem[field] ? (
				<GrideDateFormatter
					date={props.dataItem[field]}
					formatType="dd.MM.yyyy"
				/>
			) : (
				<td></td>
			),
	},
});

const amountColumn = (field: string, title: string): GridColumnProps => ({
	field,
	title,
	width: "150px",
	headerClassName: CLASSNAME.TableNumeric,
	className: `${CLASSNAME.TableNumeric} no-border`,
	filterable: false,
	cells: {
		data: (props) => <NumericalCell {...props} unit="kr" />,
		headerCell: (props) => <NumericalHeader {...props} />,
	},
});

export const accountStatementGridColumns = (
	account: AccountStatement | null,
	periods: number[],
): GridColumnProps[] => {
	const minimumPeriod = periods.length > 0 ? Math.min(...periods) : "";
	const maximumPeriod = periods.length > 0 ? Math.max(...periods) : "";

	return [
		{
			field: "voucherNumber",
			title: t("VoucherNumber"),
			width: "150px",
			filterable: false,
		},

		dateColumn("voucherDate", t("VoucherDate")),
		{
			field: "voucherType",
			title: t("VoucherType"),
			width: "150px",
			filterable: true,
			cells: {
				filterCell: (props) => {
					return (
						<HeaderThElement
							columnId={props.thProps?.columnId || ""}
							{...props.thProps}
							className={`${""} no-border td-bold`}
							style={{ textAlign: "left" }}
						>
							{t("PreviousBalance")}
						</HeaderThElement>
					);
				},

				footerCell: (props) => {
					return (
						<td
							{...props.tdProps}
							style={{ paddingRight: "20px" }}
							className={` no-border td-bold`}
						>
							{t("NewBalance")}
						</td>
					);
				},
			},
		},
		{
			field: "accountingYear",
			title: t("Year"),
			width: "100px",
			filterable: false,
			className: `${CLASSNAME.TableNumeric} no-border`,
			headerClassName: CLASSNAME.TableNumeric,

			cells: {
				filterCell: (props) => {
					return (
						<HeaderThElement
							columnId={props.thProps?.columnId || ""}
							{...props.thProps}
							style={{ paddingRight: "10px" }}
							className={`${CLASSNAME.TableNumeric} no-border td-bold`}
						>
							{account?.accountingYear ?? ""}
						</HeaderThElement>
					);
				},

				footerCell: (props) => {
					return (
						<td className={`${CLASSNAME.TableNumeric} no-border td-bold`}>
							<span style={{ marginLeft: "10px" }}>
								{account?.accountingYear ?? ""}
							</span>
						</td>
					);
				},
			},
		},
		{
			field: "accountingPeriod",
			title: t("Period"),
			width: "80px",
			filterable: true,
			className: `${CLASSNAME.TableNumeric} no-border`,
			headerClassName: CLASSNAME.TableNumeric,
			cells: {
				filterCell: (props) => {
					return (
						<HeaderThElement
							columnId={props.thProps?.columnId || ""}
							{...props.thProps}
							style={{ paddingRight: "10px" }}
							className={`${CLASSNAME.TableNumeric} no-border td-bold`}
						>
							{minimumPeriod}
						</HeaderThElement>
					);
				},

				footerCell: (props) => {
					return (
						<td className={`${CLASSNAME.TableNumeric} no-border td-bold`}>
							{maximumPeriod}
						</td>
					);
				},
			},
		},
		{
			field: "amount",
			title: t("Amount"),
			width: "170px",
			filterable: true,
			className: `${CLASSNAME.TableNumeric} no-border`,
			headerClassName: CLASSNAME.TableNumeric,
			cells: {
				data: (props) => <NumericalCell {...props} unit="kr" />,
				filterCell: (props) => {
					return (
						<HeaderThElement
							columnId={props.thProps?.columnId || ""}
							{...props.thProps}
							style={{ paddingRight: "10px" }}
							className={`${CLASSNAME.TableNumeric} no-border td-bold`}
						>
							{`${formatNorwegianNumber(account?.balanceBeforePeriod || 0, 2, 2)} kr`}
						</HeaderThElement>
					);
				},

				footerCell: (props) => {
					return (
						<td className={`${CLASSNAME.TableNumeric} no-border td-bold`}>
							{`${formatNorwegianNumber(account?.balanceAfterPeriod || 0, 2, 2)} kr`}
						</td>
					);
				},
			},
		},
		{
			filterable: false,
		},
		// amountColumn("amount", t("Amount")),
		dateColumn("dueDate", t("DueDate"), 160),
		dateColumn("paidDate", t("PaidDate"), 160),
		{
			field: "invoiceNumber",
			title: t("InvoiceNumber"),
			width: "130px",
			filterable: false,
		},
		{
			field: "crossReferenceNumber",
			title: t("CrossReferenceNumber"),
			width: "160px",
			filterable: false,
		},
		amountColumn("remainingAmount", t("RemainingAmount")),

		// amountColumn("balanceBeforePeriod", t("BalanceAfterPeriod")),
		// amountColumn("balanceAfterPeriod", t("BalanceAfterPeriod")),
	];
};
